/**
 * Tesoreria › Banche (07/10/2026): the accounts of K-Rev, those of the table
 * «Conti» of Tabelle (same code, so that invoices and payments keep using it)
 * with what the Tesoreria needs: bank, IBAN, the balance it starts from on
 * 01/01/2025, the account of the accountant to check it against.
 */
import { z } from 'zod';
import { fromCents, toCents } from './money.js';
import { ibanError, normaliseIban } from './tax-ids.js';
import { isoDateSchema, optionalText, requiredText } from './validation.js';
export const BANK_ACCOUNT_KINDS = ['Banca', 'Carta', 'Cassa', 'Virtuale'];
/** The day the balances of the Tesoreria start from (decision of 06/10/2026). */
export const BANK_OPENING_DATE = '2025-01-01';
const money = z
    .string()
    .trim()
    .regex(/^-?\d{1,12}([.,]\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value.replace(',', '.'))));
const nonNegative = (message) => money.refine((value) => toCents(value) >= 0, { message });
export const bankAccountSchema = z
    .object({
    /** The short name everybody uses: «Intesa», «BCC». */
    alias: requiredText(60),
    kind: z.enum(BANK_ACCOUNT_KINDS),
    bankName: optionalText(120),
    iban: optionalText(40).transform((value) => (value ? normaliseIban(value) : null)),
    bic: optionalText(11).transform((value) => (value ? value.toUpperCase() : null)),
    /** For a card: the account it is charged to. */
    backingCode: optionalText(30),
    openingBalance: money.nullable(),
    openingDate: isoDateSchema.nullable(),
    /** The day the account was closed (3.10): its statements are checked up to then. */
    closedOn: isoDateSchema.nullish().transform((value) => value ?? null),
    /** The account of the accountant (e.g. 18.01.02), to check the balances against. */
    ledgerAccountCode: optionalText(30),
    notes: optionalText(500),
    active: z.boolean().default(true),
    /** The overdraft granted by the bank (Scadenzario): zero when there is none. */
    creditLine: nonNegative('Il fido non può essere negativo').default('0.00'),
    creditLineExpiresOn: isoDateSchema.nullish().transform((value) => value ?? null),
    /** The balance the Scadenzario warns about when it is going to fall below. */
    safetyThreshold: nonNegative('La soglia di sicurezza non può essere negativa')
        .nullish()
        .transform((value) => value ?? null),
    /** True: the Scadenzario leaves the account out of its balances. */
    excludedFromSchedule: z.boolean().default(false),
})
    .superRefine((body, context) => {
    const error = body.iban ? ibanError(body.iban) : null;
    if (error)
        context.addIssue({ code: 'custom', path: ['iban'], message: error });
    if ((body.openingBalance === null) !== (body.openingDate === null)) {
        context.addIssue({
            code: 'custom',
            path: ['openingDate'],
            message: 'Saldo di partenza e data vanno insieme',
        });
    }
    if (body.closedOn && body.openingDate && body.closedOn < body.openingDate) {
        context.addIssue({
            code: 'custom',
            path: ['closedOn'],
            message: 'La chiusura è prima dell’apertura',
        });
    }
    if (body.kind === 'Banca' && !body.iban) {
        context.addIssue({ code: 'custom', path: ['iban'], message: 'Un conto bancario ha l’IBAN' });
    }
});
/** «IT29 R030 6951 2921 0000 0005 832»: an IBAN in groups of four, as banks print it. */
export function formatIban(iban) {
    return iban.replace(/(.{4})/g, '$1 ').trim();
}
/** The code of a new account from its alias: «Intesa» → «INTESA». */
export function bankCodeFromAlias(alias) {
    const code = alias
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 20);
    return code || 'CONTO';
}
//# sourceMappingURL=banks.js.map